import type { ResourceDictionary } from "Infrastructure/Translations/ResourceDictionary";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

const PATH_SYMBOL: unique symbol = Symbol("Resources.path");

type Primitive = string | number | boolean | bigint | symbol | null | undefined;

type PathBrand<TRoot, TLeaf> = { readonly [PATH_SYMBOL]?: [TRoot, TLeaf] };

export type ObjPathProxy<TRoot, TLeaf> = TLeaf extends Primitive
	? TLeaf & PathBrand<TRoot, TLeaf>
	: {
			[K in keyof TLeaf]-?: ObjPathProxy<TRoot, NonNullable<TLeaf[K]>>;
		} & PathBrand<TRoot, TLeaf>;

function makeProxy(path: readonly PropertyKey[]): unknown {
	return new Proxy(
		{},
		{
			get(_target, key): unknown {
				if (key === PATH_SYMBOL) return path;
				return makeProxy([...path, key]);
			},
		},
	);
}

function createProxy<TRoot>(): ObjPathProxy<TRoot, TRoot> {
	return makeProxy([]) as ObjPathProxy<TRoot, TRoot>;
}

function getPath<TRoot, TLeaf>(
	proxy: ObjPathProxy<TRoot, TLeaf>,
): readonly PropertyKey[] {
	return (
		(proxy as unknown as { [PATH_SYMBOL]?: readonly PropertyKey[] })[
			PATH_SYMBOL
		] ?? []
	);
}

export const Resources = createProxy<ResourceDictionary>();

export function getResourcePath(
	proxy: ObjPathProxy<ResourceDictionary, string>,
): string {
	const [first, ...rest] = getPath(proxy);
	if (first === undefined) return "";
	return rest.length === 0
		? String(first)
		: `${String(first)}:${rest.join(".")}`;
}

export function useResource() {
	const { t: i18Translation } = useTranslation();

	return {
		t: (
			resourcePath: ObjPathProxy<ResourceDictionary, string>,
			options?: Record<string, unknown>,
		) =>
			i18Translation(
				getResourcePath(resourcePath),
				options,
			) as unknown as string,
	};
}

/**
 * Resolves a server-error machine code (`reason`) to a localized message.
 *
 * Resolution ladder (first hit wins):
 *   1. Feature-scoped: `<parentObject>.<code>`  (e.g. `SignIn:Error.InvalidCredentials`)
 *   2. Cross-feature business reasons: `Errors:Reasons.<code>`
 *   3. FluentValidation / shared validation codes: `Validation:<code>`
 *   4. Falls back to the supplied `fallbackProperty` (typically a generic "Nastala chyba").
 *
 * `params` are passed to i18next interpolation so templates can substitute `{{minLength}}`,
 * `{{comparisonValue}}`, etc. Server provides them in `ErrorDetail.params` (camelCased keys).
 */
export function useServerError(
	parentObject: ObjPathProxy<ResourceDictionary, object>,
	fallbackProperty: ObjPathProxy<ResourceDictionary, string>,
) {
	const { t } = useResource();
	return {
		translateError: (
			code: string | null | undefined,
			params?: Record<string, unknown> | null,
		) => {
			if (!code) {
				return null;
			}

			const candidates = [
				`${getResourcePath(parentObject as unknown as ObjPathProxy<ResourceDictionary, string>)}.${code}`,
				`Errors:Reasons.${code}`,
				`Validation:${code}`,
			];

			for (const key of candidates) {
				if (i18next.exists(key)) {
					return i18next.t(key, params ?? {}) as string;
				}
			}

			const message = `Not found serverError translation for code "${code}"`;
			if (import.meta.env.DEV) {
				// biome-ignore lint/suspicious/noConsole: dev-only translation gap warning
				console.warn(`[i18n] ${message}`, { candidates });
			}
			logError(new Error(message), { code, candidates }, false);

			return t(fallbackProperty);
		},
	};
}

export function getTranslation(
	resourcePath: ObjPathProxy<ResourceDictionary, string>,
	options?: Record<string, unknown>,
): string {
	return i18next.t(
		getResourcePath(resourcePath),
		options as Record<string, unknown>,
	) as unknown as string;
}
